import type { ButtonHTMLAttributes, ReactNode } from 'react'

type Variant = 'primary' | 'secondary' | 'outline-on-dark' | 'tertiary'
type Size = 'md' | 'lg'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
  fullWidth?: boolean
  children: ReactNode
}

/**
 * DESIGN.md: every CTA is a 100px pill. Only Default and Active/Pressed are
 * documented; a very light hover transition is kept for usability.
 */
const VARIANT: Record<Variant, string> = {
  primary:
    'bg-primary text-on-primary active:bg-primary-active ' +
    'disabled:bg-primary-disabled disabled:cursor-not-allowed',
  secondary:
    'bg-surface-strong text-ink active:bg-hairline ' +
    'disabled:text-muted-soft disabled:cursor-not-allowed',
  'outline-on-dark':
    'bg-transparent text-on-dark border border-white/70 active:bg-white/10 ' +
    'disabled:opacity-50 disabled:cursor-not-allowed',
  tertiary:
    'bg-transparent text-primary active:text-primary-active ' +
    'disabled:text-muted-soft disabled:cursor-not-allowed',
}

const SIZE: Record<Size, string> = {
  md: 'h-11 px-5',
  lg: 'h-14 px-8',
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  disabled,
  children,
  className = '',
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || loading
  return (
    <button
      {...rest}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={[
        't-button rounded-[100px] inline-flex items-center justify-center gap-2',
        'transition-colors duration-150 select-none',
        VARIANT[variant],
        SIZE[size],
        fullWidth ? 'w-full' : '',
        className,
      ].join(' ')}
    >
      {loading && <Spinner />}
      {children}
    </button>
  )
}

function Spinner() {
  return (
    <svg
      className="animate-spin"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
      <path
        d="M14.5 8A6.5 6.5 0 0 0 8 1.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )
}
